import {
  Drawer,
  DrawerContainer,
  DrawerContent,
  DrawerLoader,
  EmptyState,
  Flex,
} from '@rulebooks/evidend-titan';
import { t } from '@lingui/macro';
import type { LegalEntity, PartyUpdate } from 'generated/gql';
import { useKvkUpdatesQuery } from 'generated/gql';
import { partyUpdateFieldObject } from 'helpers/partyUpdate';
import { useMemo } from 'react';

import { PartyUpdateSidebarForm } from './PartyUpdateSidebarForm';

interface PartyUpdateSidebarProps {
  legalEntity: LegalEntity;
  isSidebarShown: boolean;
  onSidebarClose: () => any;
  onUpdate?: () => any;
}
export const PartyUpdateSidebar = ({
  isSidebarShown,
  onSidebarClose,
  ...props
}: PartyUpdateSidebarProps) => {
  return (
    <Drawer isShown={isSidebarShown} position="right" width={700} onClose={onSidebarClose}>
      {isSidebarShown && <PartyUpdateSidebarContent onSidebarClose={onSidebarClose} {...props} />}
    </Drawer>
  );
};

const PartyUpdateSidebarContent = ({
  legalEntity,
  onSidebarClose,
  onUpdate,
}: Omit<PartyUpdateSidebarProps, 'isSidebarShown'>) => {
  const { data: { kvkUpdates } = {}, error } = useKvkUpdatesQuery({
    variables: {
      partyId: legalEntity.id,
    },
    // The drawer reports failures inline below, so skip the generic global banner.
    context: { suppressGlobalError: true },
  });

  const filteredUpdates = useMemo(() => {
    return (
      kvkUpdates?.filter((partyUpdate) => partyUpdateFieldObject[partyUpdate.field].label()) || []
    );
  }, [kvkUpdates]);

  const isDocumentRequired = useMemo(() => {
    return !!kvkUpdates?.find((partyUpdate) => partyUpdate.requiresDocument);
  }, [kvkUpdates]);

  const header = t`Update gegevens`;
  const subtitle = t`De wijzigingen van de gegevens worden opgehaald bij de Kamer van Koophandel.`;
  if (error)
    return (
      <DrawerContainer header={header} subtitle={subtitle}>
        <DrawerContent>
          <Flex justifyContent="center" pt={5}>
            <EmptyState
              isCentered
              description={t`De gegevens konden niet worden opgehaald bij de Kamer van Koophandel. Probeer het later opnieuw.`}
              title={t`Ophalen mislukt`}
            />
          </Flex>
        </DrawerContent>
      </DrawerContainer>
    );

  if (!kvkUpdates)
    return (
      <DrawerContainer header={header} subtitle={subtitle}>
        <DrawerContent>
          <DrawerLoader />
        </DrawerContent>
      </DrawerContainer>
    );

  return (
    <PartyUpdateSidebarForm
      header={header}
      isDocumentRequired={isDocumentRequired}
      legalEntity={legalEntity}
      partyUpdates={filteredUpdates as PartyUpdate[]}
      subtitle={subtitle}
      onSidebarClose={onSidebarClose}
      onUpdate={onUpdate}
    />
  );
};
